import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { SimulationResponseInterface } from 'store/simulation/types';
import type { PGInterface, PGRequestInterface } from './types';
import {
  DefaultWorkIndexValueVariances,
  DefaultSpecificGravityPercentVariances,
  PGTypes,
} from './types';

const initialState: PGInterface = {
  selectedPG: '',
  workIndexVariance: DefaultWorkIndexValueVariances.CONE_CRUSHER,
  specificGravityPercentVariance:
    DefaultSpecificGravityPercentVariances.CONE_CRUSHER,
  feedSizePercentVariance: DefaultSpecificGravityPercentVariances.CONE_CRUSHER,
  minSimulationResponse: null,
  maxSimulationResponse: null,
  rounding: false,
  PGRequest: {
    template: '',
    cone: {
      projectNo: '',
      goods: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      materialName: '',
      specificGravity: 0,
      f100: 0,
      f90: 0,
      f50: 0,
      f15: 0,
      workIndex: 0,
      minimumCapacity: 0,
      europeanNorm: '933-1',
      p80: 0,
      screenshotContent: '',
    },
    jaw: {
      projectNo: '',
      opportunityNo: '',
      goods: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      f100: '',
      f80: '',
      css: '',
      cssPercent: '',
      cssHalf: '',
      cssHalfPercent: '',
      workIndex: '',
      minimumCapacity: '',
      europeanNorm: '',
      pValue: '',
      pPercent: '',
      screenshotContent: '',
    },
    [PGTypes.JAW_CRUSHER_WITH_FEEDING_EQUIPMENT]: {
      projectNo: '',
      opportunityNo: '',
      goods: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      f100: '',
      f80: '',
      css: '',
      cssPercent: '',
      cssHalf: '',
      cssHalfPercent: '',
      separationTopDeck: '',
      separationTopDeckPercent: '',
      separationSecondDeck: '',
      separationSecondDeckPercent: '',
      workIndex: '',
      minimumCapacity: '',
      europeanNorm: '',
      pValue: '',
      pPercent: '',
      screenshotContent: '',
    },
    [PGTypes.VSI_CRUSHER]: {
      projectNo: '',
      opportunityNo: '',
      goods: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      f100: '',
      f85: '',
      f50: '',
      separation4mmPercent: '',
      separation2mmPercent: '',
      workIndex: '',
      feedMaterial: '',
      sif1: '',
      sif2: '',
      modelName: '',
      minimumCapacity: '',
      maximumCapacity: '',
      screenshotContent: '',
    },
    [PGTypes.MINING_APPLICATION_TYPE_ONE]: {
      projectNo: '',
      opportunityNo: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      f100: '',
      f80: '',
      css: '',
      cssPercent: '',
      cssHalf: '',
      cssHalfPercent: '',
      separationFinalScreen: '',
      separationFinalScreenPercent: '',
      workIndex: '',
      minimumCapacity: '',
      europeanNorm: '',
      pValue: '',
      pPercent: '',
      screenshotContent: '',
    },
    [PGTypes.MINING_APPLICATION_TYPE_TWO]: {
      projectNo: '',
      opportunityNo: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      f100: '',
      f80: '',
      separationTopDeck: '',
      separationTopDeckPercent: '',
      separationFinalScreen: '',
      separationFinalScreenPercent: '',
      separationFinalScreenHalf: '',
      separationFinalScreenHalfPercent: '',
      workIndex: '',
      minimumCapacity: '',
      europeanNorm: '',
      pValue: '',
      pPercent: '',
      screenshotContent: '',
    },
    [PGTypes.SCREENS]: {
      projectNo: '',
      opportunityNo: '',
      goods: '',
      certifiedIssuer: '',
      flowsheetNo: '',
      lastSimulation: '',
      layoutDrawingNo: '',
      layoutDrawingDate: '',
      materialName: '',
      specificGravity: '',
      separationTopDeck: '',
      separationTopDeckOneAndAHalf: '',
      sepTopDeckOAHPercent: '',
      separationFirstDeck: '',
      sepFirstDeckPercent: '',
      separationSecondDeck: '',
      sepSecondDeckPercent: '',
      separationThirdDeck: '',
      sepThirdDeckPercent: '',
      separationFourthDeck: '',
      sepFourthDeckPercent: '',
      separationLowerDeckHalf: '',
      sepLowerDeckHalfPercent: '',
      minimumCapacity: '',
      maximumCapacity: '',
      screenshotContent: '',
    },
  },
};

const performanceguaranteeSlice = createSlice({
  name: 'performanceguarantee',
  initialState,
  reducers: {
    setSelectedPerformanceGuarantee(state, action: PayloadAction<string>) {
      state.selectedPG = action.payload;
    },
    setPerformanceGuaranteeRounding(state, action: PayloadAction<boolean>) {
      state.rounding = action.payload;
    },
    setPerformanceGuarantee(state, action: PayloadAction<PGInterface>) {
      state.workIndexVariance = action.payload.workIndexVariance;
      state.specificGravityPercentVariance =
        action.payload.specificGravityPercentVariance;
      state.feedSizePercentVariance = action.payload.feedSizePercentVariance;
    },
    setMinSimulationResult(
      state,
      action: PayloadAction<SimulationResponseInterface>,
    ) {
      state.minSimulationResponse = action.payload;
    },
    setMaxSimulationResult(
      state,
      action: PayloadAction<SimulationResponseInterface>,
    ) {
      state.maxSimulationResponse = action.payload;
    },
    setPGRequest(state, action: PayloadAction<PGRequestInterface>) {
      state.PGRequest = action.payload;
    },
  },
});

export const {
  setMaxSimulationResult,
  setMinSimulationResult,
  setPGRequest,
  setPerformanceGuarantee,
  setPerformanceGuaranteeRounding,
  setSelectedPerformanceGuarantee,
} = performanceguaranteeSlice.actions;

export const performanceguaranteeReducer = performanceguaranteeSlice.reducer;
